import * as React from 'react'

import { Grid } from '~ui/components/layout/Box'
import { Stack } from '~ui/components/layout/Stack'
import { Skeleton } from '~ui/components/loadingSkeletons/Skeleton'
import { H2 } from '~ui/components/typography/Heading/Heading'
import { Text } from '~ui/components/typography/Text/Text'
import { TSingleSpace } from '~ui/styleGuide/spaces'
import { breakpoints } from '~ui/styleGuide/styles'
import { TRespFontByWithOptions, TResponsiveBreakpointsProps } from '~ui/utils/responsiveFonts'

type TProps = RequiredChildren &
  TResponsiveBreakpointsProps &
  SkeletonProps & {
    /**
     * category id to be passed to the category's outer Flex container
     */
    id?: ID
    /**
     * ware category name label
     */
    title: string
    /**
     * ware category description label
     */
    descriptionLabel?: Nullable<string>
    /**
     * gap between the name (or description) label and the products (children)
     */
    headerMarginBottom?: TSingleSpace
    /**
     * left padding of ware items (children), used to align the category heading
     */
    leftPadding?: TSingleSpace
    /**
     * right padding of ware items (children), used to align the category heading
     */
    rightPadding?: TSingleSpace
    /**
     * space between ware items
     */
    spacing?: PropsOf<typeof Stack>['spacing']
    /**
     * object with maxSize and minSize key-value pairs, that specify the category heading fontSize for maxBreakpoint and minBreakpoint respectively
     */
    headingFontSizeLimits?: Pick<TRespFontByWithOptions, 'maxSize' | 'minSize'>
  }

export const WareCategory: React.FC<TProps> = ({
  id,
  children,
  title,
  descriptionLabel,
  leftPadding = `none`,
  rightPadding = `none`,
  spacing = `medium`,
  headerMarginBottom = `large`,
  responsiveBreakpoints = {
    maxBreakpoint: breakpoints.lg,
    minBreakpoint: breakpoints.sm,
  },
  responsiveUnit = 'px',
  headingFontSizeLimits,
  showSkeleton = false,
}) => {
  const { maxBreakpoint, minBreakpoint } = responsiveBreakpoints

  return (
    <Grid rowGap={headerMarginBottom} id={id}>
      <Grid
        rowGap={`extraSmall`}
        padding={{
          top: `none`,
          right: rightPadding,
          bottom: `none`,
          left: leftPadding,
        }}
      >
        <Skeleton showSkeleton={showSkeleton} maxWidth={`15rem`}>
          <H2
            fontStyle={`boldExtra`}
            responsiveSize={{
              unit: responsiveUnit,
              maxBreakpoint,
              maxSize: headingFontSizeLimits?.maxSize ?? 24,
              minBreakpoint,
              minSize: headingFontSizeLimits?.minSize ?? 20,
            }}
            truncateOnLines={0}
          >
            {title}
          </H2>
        </Skeleton>
        {descriptionLabel && (
          <Text contrast={`low`} truncateOnLines={4}>
            {descriptionLabel}
          </Text>
        )}
      </Grid>
      <Stack spacing={spacing} dividerProps={{ thickness: `1px` }}>
        {children}
      </Stack>
    </Grid>
  )
}
